import {
  createDefaultAction,
  createDefaultRule,
  createDefaultTrigger,
} from "./types";
import { syncAgentRulesWithActions } from "./syncAgentRules";
import { ensureSkillsConsumerActionsOnAgent } from "../../constants/builtInActionApps";

const asArray = (value) => (Array.isArray(value) ? value : []);

const normalizeTrigger = (trigger) => {
  const next = {
    ...createDefaultTrigger(),
    ...trigger,
  };
  // Prefer real app slug (app/source) over display label so drafts match existing triggers.
  const slug = String(next.app || next.source || "").toLowerCase().trim();
  if (slug && slug !== "workflow_event") {
    next.app = next.app || slug;
    if (!next.source || next.source === "workflow_event") next.source = slug;
  }
  return next;
};

const normalizeAction = (action) => ({
  ...createDefaultAction(),
  ...action,
});

/** App slug for matching — never use display labels like "DrChrono" when source/app exist. */
const appKey = (a) =>
  (a?.app || a?.source || a?.config?.app || a?.app_label || "")
    .toString()
    .toLowerCase()
    .trim();

const triggerEventKey = (t) =>
  (t?.trigger_key || t?.type || t?.trigger_action || t?.performList || "")
    .toString()
    .toLowerCase()
    .trim();

const triggerSignature = (t) => {
  const app = appKey(t);
  const key = triggerEventKey(t);
  return `${app}|${key}`;
};

/** Higher = more complete polling/webhook setup (prefer when collapsing duplicates). */
const triggerCompletenessScore = (t) => {
  if (!t || typeof t !== "object") return 0;
  let score = 0;
  if (t.app) score += 3;
  if (t.performList || t.trigger_action || t.triggerResource) score += 3;
  if (t.pollingParams && typeof t.pollingParams === "object" && Object.keys(t.pollingParams).length > 0) {
    score += 3;
  }
  if (String(t.event_type || "").toLowerCase() === "polling") score += 2;
  if (t.credential) score += 1;
  if (Array.isArray(t.fields) && t.fields.length > 0) score += 1;
  if (t.authname) score += 1;
  return score;
};

/**
 * Agents support a single trigger. Collapse duplicates (same app+event) and keep the richest one.
 * Exported so save/load can heal agents that already have stacked triggers from older copilot merges.
 */
export const dedupeAgentTriggers = (triggers = []) => {
  const list = asArray(triggers).map(normalizeTrigger).filter(Boolean);
  if (list.length <= 1) return list;

  const bySig = new Map();
  for (const t of list) {
    const sig = triggerSignature(t);
    const key = sig && sig !== "|" ? sig : `id:${t.trigger_id || Math.random()}`;
    const prev = bySig.get(key);
    if (!prev || triggerCompletenessScore(t) >= triggerCompletenessScore(prev)) {
      bySig.set(key, prev ? { ...t, trigger_id: prev.trigger_id || t.trigger_id } : t);
    }
  }
  const unique = Array.from(bySig.values());
  if (unique.length <= 1) return unique;

  // Platform limit: one trigger — keep the most complete remaining entry.
  unique.sort((a, b) => triggerCompletenessScore(b) - triggerCompletenessScore(a));
  return [unique[0]];
};

const actionSignature = (a) => {
  const app = appKey(a);
  const key = (a?.action_key || a?.config?.action || "").toString().toLowerCase().trim();
  return `${app}|${key}`;
};

const actionLabelKey = (a) =>
  (a?.label || a?.action_label || a?.action_key || a?.config?.action || "")
    .toString()
    .toLowerCase()
    .trim();

const fieldName = (f) => (f?.name || f?.title || "").toString().toLowerCase().trim();

const fieldValueIsEmptyStub = (value) => {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") {
    const s = value.trim();
    if (!s) return true;
    if (/^let agent pick value$/i.test(s)) return true;
    if (/^\[configured/i.test(s)) return true;
    if (/do not rewrite/i.test(s)) return true;
  }
  return false;
};

/**
 * Merge draft fields onto existing. Never wipe substantial configured values
 * (especially Workload Code python_code) with empty / "Let agent pick" stubs.
 */
const mergeActionFields = (draftFields, existingFields) => {
  const draft = asArray(draftFields);
  const existing = asArray(existingFields);
  if (draft.length === 0) return existing;
  if (existing.length === 0) return draft;

  const byName = new Map();
  existing.forEach((f) => {
    const n = fieldName(f);
    if (n) byName.set(n, f);
  });

  const used = new Set();
  const out = draft.map((df) => {
    const n = fieldName(df);
    const ex = n ? byName.get(n) : null;
    if (n) used.add(n);
    if (!ex) return df;

    const draftVal = df?.value;
    const exVal = ex?.value;
    const isCodeField = n === "python_code" || n === "python" || n === "code";
    const exLen = typeof exVal === "string" ? exVal.length : 0;
    const draftLen = typeof draftVal === "string" ? draftVal.length : 0;

    // Keep existing configured code / long values when draft is stub or much shorter.
    if (
      fieldValueIsEmptyStub(draftVal) ||
      (isCodeField && exLen > 40 && draftLen < Math.max(40, exLen * 0.5))
    ) {
      return {
        ...ex,
        ...df,
        value: exVal,
        ...(ex.schema && !df.schema ? { schema: ex.schema } : {}),
        ...(ex.in && !df.in ? { in: ex.in } : {}),
      };
    }
    return { ...ex, ...df };
  });

  // Preserve existing fields the draft omitted entirely (e.g. python_code dropped).
  existing.forEach((ex) => {
    const n = fieldName(ex);
    if (!n || used.has(n)) return;
    if (fieldValueIsEmptyStub(ex?.value) && n !== "python_code") return;
    out.push(ex);
  });
  return out;
};

/**
 * Prefer action_id, then same app|key + similar label, then first unused same app|key.
 * Multiple Workload Code runPython steps must not all match the same draft row.
 */
const findMatchingDraftAction = (existing, draftPool) => {
  if (!existing || !draftPool?.length) return null;
  const exId = existing.action_id;
  if (exId) {
    const byId = draftPool.find((d) => d?.action_id && d.action_id === exId);
    if (byId) return byId;
  }
  const sig = actionSignature(existing);
  const sameSig = draftPool.filter((d) => actionSignature(d) === sig);
  if (sameSig.length === 0) return null;
  if (sameSig.length === 1) return sameSig[0];

  const exLabel = actionLabelKey(existing);
  if (exLabel) {
    const labelHit = sameSig.find((d) => {
      const dl = actionLabelKey(d);
      return dl && (dl === exLabel || dl.includes(exLabel) || exLabel.includes(dl));
    });
    if (labelHit) return labelHit;
  }
  return sameSig[0];
};

/** Preserve credential, authname, url, and fields from existing triggers when applying a draft. */
const mergePreservedTriggerAuth = (draftTriggers, existingTriggers) => {
  if (!Array.isArray(existingTriggers) || existingTriggers.length === 0) return draftTriggers;
  const byId = new Map(existingTriggers.map((t) => [t?.trigger_id, t]).filter(([id]) => id));
  const bySig = new Map(
    existingTriggers.map((t) => [triggerSignature(t), t]).filter(([sig]) => sig && sig !== "|")
  );
  return (draftTriggers || []).map((trigger) => {
    const existingById = trigger?.trigger_id ? byId.get(trigger.trigger_id) : null;
    const existing = existingById || (triggerSignature(trigger) ? bySig.get(triggerSignature(trigger)) : null);
    if (!existing) return trigger;
    const preservedCredential = existing.credential ?? "";
    const preservedAuthname = existing.authname ?? "";
    const preservedUrl = existing.url ?? "";
    const preservedTestData = existing.test_data ?? existing.testData;
    const preservedTriggerMessage = existing.triggerMessage;
    const preservedFields =
      existingById && Array.isArray(existing.fields) && existing.fields.length > 0
        ? existing.fields
        : undefined;
    return {
      ...trigger,
      trigger_id: existing.trigger_id || trigger.trigger_id,
      credential: preservedCredential || trigger.credential || "",
      authname: preservedAuthname || trigger.authname || "",
      url: preservedUrl || trigger.url || "",
      ...(preservedTestData != null ? { test_data: preservedTestData } : {}),
      ...(preservedTriggerMessage != null ? { triggerMessage: preservedTriggerMessage } : {}),
      ...(preservedFields && (!trigger.fields || trigger.fields.length === 0)
        ? { fields: preservedFields }
        : {}),
    };
  });
};

const mergeTriggerFields = (draftFields, existingFields) => {
  if (!Array.isArray(draftFields) || draftFields.length === 0) return existingFields || [];
  if (!Array.isArray(existingFields) || existingFields.length === 0) return draftFields;
  const byName = new Map(existingFields.map((f) => [f?.name, f]).filter(([n]) => n));
  return draftFields.map((df) => {
    const ex = byName.get(df?.name);
    if (!ex) return df;
    const draftVal = df?.value;
    const keepExisting =
      draftVal === undefined || draftVal === null || draftVal === "" || draftVal === "Choose an event";
    return keepExisting ? { ...ex, ...df, value: ex.value } : { ...ex, ...df };
  });
};

const findMatchingExistingTrigger = (draftTrigger, existingTriggers) => {
  const sig = triggerSignature(draftTrigger);
  if (sig && sig !== "|") {
    const exact = existingTriggers.find((t) => triggerSignature(t) === sig);
    if (exact) return exact;
  }
  const draftApp = appKey(draftTrigger);
  if (!draftApp) return existingTriggers[0] || null;
  // Same app, any event — agents only allow one trigger, so treat as replace-in-place.
  return existingTriggers.find((t) => appKey(t) === draftApp) || existingTriggers[0] || null;
};

/**
 * Merge draft triggers into existing. Agents support only one trigger: never append a second.
 * Same app+event updates in place; a new event replaces the previous trigger (auth preserved when possible).
 */
const mergeTriggersIncremental = (draftTriggers, existingTriggers) => {
  const draft = dedupeAgentTriggers(draftTriggers);
  const existing = dedupeAgentTriggers(existingTriggers);
  if (existing.length === 0) return mergePreservedTriggerAuth(draft, existing);
  if (draft.length === 0) return existing;

  const dt = draft[0];
  const ex = findMatchingExistingTrigger(dt, existing);
  const mergedOne = mergePreservedTriggerAuth(
    [
      {
        ...dt,
        trigger_id: ex?.trigger_id || dt.trigger_id,
        fields: mergeTriggerFields(dt.fields, ex?.fields),
        test_data: ex?.test_data ?? dt.test_data,
        triggerMessage: ex?.triggerMessage ?? dt.triggerMessage,
        // Prefer complete polling metadata from whichever side has it.
        performList: dt.performList || ex?.performList || "",
        triggerResource: dt.triggerResource || ex?.triggerResource || "",
        trigger_action: dt.trigger_action || ex?.trigger_action || "",
        event_type: dt.event_type || ex?.event_type || "",
        pollingParams:
          (dt.pollingParams && Object.keys(dt.pollingParams).length > 0
            ? dt.pollingParams
            : ex?.pollingParams) || {},
      },
    ],
    ex ? [ex] : existing
  )[0];
  return dedupeAgentTriggers([mergedOne]);
};

const mergeActionsIncremental = (draftActions, existingActions) => {
  const draft = asArray(draftActions).map(normalizeAction);
  const existing = asArray(existingActions);
  if (existing.length === 0) return mergePreservedActionAuth(draft, existing);
  if (draft.length === 0) return existing;

  const draftPool = [...draft];
  const merged = existing.map((ex) => {
    const da = findMatchingDraftAction(ex, draftPool);
    if (!da) return ex;
    const idx = draftPool.indexOf(da);
    if (idx >= 0) draftPool.splice(idx, 1);

    const mergedFields = mergeActionFields(
      da.fields || da.config?.fields,
      ex.fields || ex.config?.fields
    );
    const patched = {
      ...da,
      action_id: ex.action_id,
      fields: mergedFields,
      config: {
        ...(typeof da.config === "object" && da.config ? da.config : {}),
        fields: mergedFields,
        action: da.config?.action || da.action_key || ex.config?.action || ex.action_key,
        app: da.config?.app || da.app || ex.config?.app || ex.app,
      },
      // Keep the user's label unless the draft clearly renamed this tool.
      label: da.label && da.label !== ex.label ? da.label : ex.label || da.label,
      action_label: da.action_label || ex.action_label || da.label || ex.label,
    };
    return mergePreservedActionAuth([patched], [ex])[0];
  });

  // Append truly new draft actions (unmatched).
  draftPool.forEach((da) => {
    merged.push(mergePreservedActionAuth([da], existing)[0]);
  });
  return merged;
};

const mergeRulesIncremental = (draftRules, existingRules, mergedActions) => {
  const normalized = asArray(draftRules).map((rule) => normalizeRule(rule, mergedActions));
  const existing = asArray(existingRules);
  if (normalized.length === 0) return existing;
  if (existing.length === 0) return normalized;

  const draftFirst = normalized[0];
  const existingIds = new Set(existing.flatMap((r) => r?.action_ids || []));
  const newIds = (draftFirst.action_ids || []).filter((id) => id && !existingIds.has(id));

  return existing.map((rule, i) => {
    if (i !== 0 || rule?.enabled === false) return rule;
    const action_ids =
      newIds.length > 0 ? [...(rule.action_ids || []), ...newIds] : rule.action_ids || draftFirst.action_ids;
    return {
      ...rule,
      name: draftFirst.name || rule.name,
      if: draftFirst.if && String(draftFirst.if).trim() && draftFirst.if !== "true" ? draftFirst.if : rule.if,
      action_ids: action_ids?.length ? action_ids : rule.action_ids,
    };
  });
};

const shouldFullReplaceDraft = (draft, agent, section) => {
  if (section !== "all") return false;
  if (draft?.merge_mode === "replace") return true;
  if (draft?.merge_mode === "incremental") return false;
  const hasExisting = (agent?.actions || []).length > 0 || (agent?.triggers || []).length > 0;
  return !hasExisting;
};

/** Preserve credential, authname, url, and config from existing actions when applying a draft.
 * 1) Match by action_id (exact update).
 * 2) If no match (new AI-created action), use latest credential/authname for the same app from any existing action.
 */
const mergePreservedActionAuth = (draftActions, existingActions) => {
  if (!Array.isArray(existingActions) || existingActions.length === 0) return draftActions;
  const byId = new Map(existingActions.map((a) => [a?.action_id, a]).filter(([id]) => id));
  const byApp = new Map();
  existingActions.forEach((a) => {
    const key = appKey(a);
    if (!key) return;
    const cred = a.credential ?? a.config?.credential ?? "";
    if (!byApp.has(key) || cred) byApp.set(key, a);
  });
  return (draftActions || []).map((action) => {
    const existingById = action?.action_id ? byId.get(action.action_id) : null;
    const existing = existingById || (appKey(action) ? byApp.get(appKey(action)) : null);
    if (!existing) return action;
    const preservedCredential = existing.credential ?? existing.config?.credential ?? "";
    const preservedAuthname = existing.authname ?? existing.config?.authname ?? "";
    const preservedUrl = existing.url ?? "";
    const preservedConfigCredential = existing.config?.credential ?? "";
    const preservedConfigAuthname = existing.config?.authname ?? "";
    const existingFields =
      Array.isArray(existing.config?.fields) && existing.config.fields.length > 0
        ? existing.config.fields
        : Array.isArray(existing.fields) && existing.fields.length > 0
        ? existing.fields
        : [];
    const draftFields =
      Array.isArray(action.config?.fields) && action.config.fields.length > 0
        ? action.config.fields
        : Array.isArray(action.fields)
        ? action.fields
        : [];
    // Match by action_id OR same tool row: always field-merge so python_code survives.
    const mergedFields =
      existingById || actionSignature(action) === actionSignature(existing)
        ? mergeActionFields(draftFields, existingFields)
        : draftFields.length > 0
        ? draftFields
        : existingFields;
    return {
      ...action,
      credential: preservedCredential || action.credential || "",
      authname: preservedAuthname || action.authname || "",
      url: preservedUrl || action.url || "",
      fields: mergedFields,
      config: {
        ...(action.config && typeof action.config === "object" ? action.config : {}),
        credential: preservedConfigCredential || (action.config && action.config.credential) || "",
        authname: preservedConfigAuthname || (action.config && action.config.authname) || "",
        fields: mergedFields,
      },
    };
  });
};

const normalizeRule = (rule, actions = []) => {
  const nextRule = {
    ...createDefaultRule(),
    ...rule,
  };
  if (asArray(nextRule.action_ids).length > 0) return nextRule;
  const firstActionId = actions[0]?.action_id;
  return {
    ...nextRule,
    action_ids: firstActionId ? [firstActionId] : [],
  };
};

const isObject = (value) => value && typeof value === "object" && !Array.isArray(value);

export const parseProposalFromText = (text) => {
  if (!text || typeof text !== "string") return null;
  const firstBrace = text.indexOf("{");
  const lastBrace = text.lastIndexOf("}");
  if (firstBrace === -1 || lastBrace <= firstBrace) return null;
  const maybeJson = text.slice(firstBrace, lastBrace + 1);
  try {
    const parsed = JSON.parse(maybeJson);
    return isObject(parsed) ? parsed : null;
  } catch (err) {
    return null;
  }
};

export const normalizeDraftProposal = (proposal = {}) => {
  if (!isObject(proposal)) return null;
  const actions = asArray(proposal.actions).map(normalizeAction);
  const normalized = {
    name: proposal.name || "",
    description: proposal.description || "",
    goal: proposal.goal || "",
    triggers: asArray(proposal.triggers).map(normalizeTrigger),
    actions,
    rules: asArray(proposal.rules).map((rule) => normalizeRule(rule, actions)),
    policy: isObject(proposal.policy) ? proposal.policy : {},
    merge_mode: proposal.merge_mode || proposal.mergeMode || "",
  };
  return normalized;
};

export const applyDraftToAgent = (agent, draft, section = "all") => {
  const normalized = normalizeDraftProposal(draft);
  if (!normalized) return agent;

  if (section === "goal") {
    return {
      ...agent,
      name: normalized.name || agent.name,
      description: normalized.description || agent.description,
      // Instructions (goal) are user-edited only; copilot does not replace them.
      goal: agent.goal,
    };
  }

  if (section === "watches") {
    return {
      ...agent,
      triggers: dedupeAgentTriggers(
        mergeTriggersIncremental(normalized.triggers, agent.triggers || [])
      ),
    };
  }

  if (section === "actions") {
    // Never drop user-added tools — only update matches and append new draft actions.
    const merged = mergeActionsIncremental(normalized.actions, agent.actions || []);
    return syncAgentRulesWithActions({ ...agent, actions: merged });
  }

  if (section === "rules") {
    return {
      ...agent,
      rules: mergeRulesIncremental(normalized.rules, agent.rules || [], agent.actions || []),
    };
  }

  if (section === "policy") {
    return { ...agent, policy: { ...(agent.policy || {}), ...(normalized.policy || {}) } };
  }

  const fullReplace = shouldFullReplaceDraft(normalized, agent, section);
  const mergedTriggers = dedupeAgentTriggers(
    fullReplace
      ? mergePreservedTriggerAuth(normalized.triggers, agent.triggers || [])
      : mergeTriggersIncremental(normalized.triggers, agent.triggers || [])
  );
  // Actions: always incremental when the agent already has tools (user may have added extras the model omits).
  const hasExistingActions = (agent.actions || []).length > 0;
  const mergedActions = hasExistingActions || !fullReplace
    ? mergeActionsIncremental(normalized.actions, agent.actions || [])
    : mergePreservedActionAuth(normalized.actions, agent.actions || []);
  const mergedRules = fullReplace && !hasExistingActions
    ? normalized.rules
    : mergeRulesIncremental(normalized.rules, agent.rules, mergedActions);

  const merged = syncAgentRulesWithActions({
    ...agent,
    name: normalized.name || agent.name,
    description: normalized.description || agent.description,
    // Instructions (goal) are user-edited only; copilot does not replace them.
    goal: agent.goal,
    triggers: mergedTriggers,
    actions: mergedActions,
    rules: mergedRules,
    policy: { ...(agent.policy || {}), ...(normalized.policy || {}) },
  });
  // Skill-backed drafts: persist skills/pages tools so Preview Approve can run them
  // (brain injects these at plan time even when the form Tools list was empty).
  try {
    const uid =
      (typeof localStorage !== "undefined" && localStorage.getItem("user")) ||
      agent.created_by_user_id ||
      "";
    return ensureSkillsConsumerActionsOnAgent(merged, { userId: uid });
  } catch {
    return merged;
  }
};
